import React from 'react';
import { ExclamationTriangleIcon, PhotoIcon } from '@heroicons/react/24/outline';

interface CameraTroubleshootingProps {
  onClose: () => void;
  onUsePhotoLibrary: () => void;
}

const CameraTroubleshooting: React.FC<CameraTroubleshootingProps> = ({
  onClose,
  onUsePhotoLibrary,
}) => {
  const getBrowserInstructions = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (userAgent.includes('chrome')) {
      return {
        name: 'Chrome',
        steps: [
          'Click the camera icon in the address bar',
          'Select "Always allow" for camera access',
          'Refresh the page and try again'
        ]
      };
    } else if (userAgent.includes('firefox')) {
      return {
        name: 'Firefox',
        steps: [
          'Click the shield icon in the address bar',
          'Click "Allow" for camera permissions',
          'Refresh the page and try again'
        ]
      };
    } else if (userAgent.includes('safari')) {
      return {
        name: 'Safari',
        steps: [
          'Go to Safari → Settings → Websites',
          'Find Camera and set to "Allow"',
          'Refresh the page and try again'
        ]
      };
    } else if (userAgent.includes('edge')) {
      return {
        name: 'Edge',
        steps: [
          'Click the camera icon in the address bar',
          'Select "Always allow" for camera access',
          'Refresh the page and try again'
        ]
      };
    }
    
    return {
      name: 'Your Browser',
      steps: [
        'Look for camera permission prompts',
        'Allow camera access when prompted',
        'Refresh the page if needed'
      ]
    };
  };

  const browserInfo = getBrowserInstructions();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
      <div className="relative bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-2">
            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />
            <h3 className="text-lg font-semibold">Camera Access Issue</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            ✕
          </button>
        </div>

        <div className="space-y-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Quick Fix for {browserInfo.name}:</h4>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              {browserInfo.steps.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">General Troubleshooting:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Make sure no other app is using your camera</li>
              <li>• Check if your camera is enabled in device settings</li>
              <li>• Try using a different browser</li>
              <li>• Restart your browser completely</li>
              <li>• Check if your camera drivers are up to date</li>
            </ul>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="font-medium text-yellow-900 mb-2">Mobile Devices:</h4>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li>• Go to Settings → Apps → [Your Browser] → Permissions</li>
              <li>• Enable Camera permission</li>
              <li>• Make sure the app has camera access</li>
              <li>• Try switching between front and back camera</li>
            </ul>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={onUsePhotoLibrary}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              <PhotoIcon className="h-5 w-5" />
              <span>Use Photo Library Instead</span>
            </button>
            <button
              onClick={onClose}
              className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CameraTroubleshooting;
